import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Plus, Search, Pencil, Trash2, X, Mail, Phone, Building, Calendar, Send, ClipboardList, ChevronRight, FileText } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { tenantService } from '../services/tenantService';
import { propertyService } from '../services/propertyService';
import ActivityFeed from './ActivityFeed';
import LeaseSetupModal from './LeaseSetupModal';

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [leaseTenant, setLeaseTenant] = useState(null);
  const [activityTenant, setActivityTenant] = useState(null); // tenant whose activity panel is open
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyId: '',
    unitNumber: '',
    leaseStart: '',
    leaseEnd: '',
    status: 'Active'
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [invitingId, setInvitingId] = useState(null);

  useEffect(() => {
    loadTenants();
    loadProperties();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getAllTenants();
      setTenants(data);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const data = await propertyService.getAll();
      setProperties(data);
    } catch (error) {
      console.error('Failed to load properties:', error);
    }
  };

  const handleInviteTenant = async (tenant) => {
    setInvitingId(tenant._id);
    setError('');
    try {
      const { invitationStatus, inviteCode } = await tenantService.inviteTenant(tenant._id);
      setTenants((prev) => prev.map((t) => t._id === tenant._id ? { ...t, invitationStatus, invitationCode: inviteCode } : t));
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send invite');
    } finally {
      setInvitingId(null);
    }
  };

  const handleAddTenant = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      propertyId: '',
      unitNumber: '',
      leaseStart: '',
      leaseEnd: '',
      status: 'Active'
    });
    setError('');
    setIsAddModalOpen(true);
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      email: tenant.email,
      phone: tenant.phone || '',
      propertyId: tenant.property._id,
      unitNumber: tenant.unitNumber,
      leaseStart: tenant.leaseStart ? new Date(tenant.leaseStart).toISOString().split('T')[0] : '',
      leaseEnd: tenant.leaseEnd ? new Date(tenant.leaseEnd).toISOString().split('T')[0] : '',
      status: tenant.status
    });
    setError('');
    setIsEditModalOpen(true);
  };

  const handleDeleteTenant = (tenant) => {
    setSelectedTenant(tenant);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await tenantService.createTenant(formData);
      setIsAddModalOpen(false);
      loadTenants();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await tenantService.updateTenant(selectedTenant._id, formData);
      setIsEditModalOpen(false);
      loadTenants();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setSubmitting(true);
    try {
      await tenantService.deleteTenant(selectedTenant._id);
      setIsDeleteModalOpen(false);
      loadTenants();
    } catch (error) {
      console.error('Failed to delete tenant:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.unitNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'Past':
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const renderTenantFormFields = () => (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-800 px-4 py-3 rounded relative text-sm">
          {error}
        </div>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
          disabled={submitting}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          required
          disabled={submitting}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone</Label>
        <Input
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          disabled={submitting}
          placeholder="(555) 123-4567"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="propertyId">Property *</Label>
        <select
          id="propertyId"
          name="propertyId"
          value={formData.propertyId}
          onChange={handleInputChange}
          required
          disabled={submitting}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">Select a property</option>
          {properties.map(property => (
            <option key={property._id} value={property._id}>
              {property.name} - {property.address}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="unitNumber">Unit Number *</Label>
        <Input
          id="unitNumber"
          name="unitNumber"
          value={formData.unitNumber}
          onChange={handleInputChange}
          required
          disabled={submitting}
          placeholder="e.g., 101, 2B"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="leaseStart">Lease Start</Label>
          <Input
            id="leaseStart"
            name="leaseStart"
            type="date"
            value={formData.leaseStart}
            onChange={handleInputChange}
            disabled={submitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="leaseEnd">Lease End</Label>
          <Input
            id="leaseEnd"
            name="leaseEnd"
            type="date"
            value={formData.leaseEnd}
            onChange={handleInputChange}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="status">Status *</Label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleInputChange}
          required
          disabled={submitting}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Past">Past</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* ── Main list ─────────────────────────────────────────────── */}
      <div className={`space-y-6 transition-all duration-300 ${activityTenant ? 'lg:flex-1 lg:min-w-0' : 'w-full'}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Tenants</h2>
        <Button onClick={handleAddTenant}>
          <Plus className="mr-2 h-4 w-4" /> Add Tenant
        </Button>
      </div>

      <div className="flex items-center space-x-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search tenants..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {error && !isAddModalOpen && !isEditModalOpen && !isDeleteModalOpen && (
        <div className="bg-red-50 text-red-800 px-4 py-3 rounded text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredTenants.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">
              {searchQuery ? 'No tenants found matching your search.' : 'No tenants yet. Click "Add Tenant" to get started.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border">
          <div className="p-4">
            <div className="space-y-4">
              {filteredTenants.map((tenant) => (
                <div key={tenant._id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <Avatar className="flex-shrink-0">
                      <AvatarFallback>{getInitials(tenant.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{tenant.name}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <Building className="h-3 w-3 flex-shrink-0" />
                          Unit {tenant.unitNumber}
                        </span>
                        <span className="whitespace-nowrap">{tenant.property?.name || 'N/A'}</span>
                        {tenant.email && (
                          <span className="flex items-center gap-1 min-w-0">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{tenant.email}</span>
                          </span>
                        )}
                        {tenant.phone && (
                          <span className="flex items-center gap-1 whitespace-nowrap">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            {tenant.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {/* Badges row */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors border-transparent ${getStatusColor(tenant.status)}`}>
                        {tenant.status}
                      </span>
                      {tenant.invitationStatus && tenant.invitationStatus !== 'NotSent' && (
                        <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-800 px-2.5 py-0.5 text-xs font-semibold">
                          Invite: {tenant.invitationStatus}
                        </span>
                      )}
                    </div>
                    {/* Action buttons row */}
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant={activityTenant?._id === tenant._id ? 'secondary' : 'outline'}
                        size="sm"
                        onClick={() => setActivityTenant(prev => prev?._id === tenant._id ? null : tenant)}
                        className="gap-1.5"
                      >
                        <ClipboardList className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Activity</span>
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleInviteTenant(tenant)}
                        disabled={invitingId === tenant._id || tenant.invitationStatus === 'Accepted'}
                        title={tenant.invitationStatus === 'Pending' ? 'Resend Invite' : 'Send Invite'}
                      >
                        <Send className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline ml-1.5">
                          {invitingId === tenant._id ? 'Sending…' : tenant.invitationStatus === 'Pending' ? 'Resend' : 'Invite'}
                        </span>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setLeaseTenant(tenant)} title="Manage Lease">
                        <FileText className="h-4 w-4 text-cyan-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEditTenant(tenant)} title="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTenant(tenant)} title="Delete">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Tenant Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Tenant</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitAdd}>
            {renderTenantFormFields()}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Adding...' : 'Add Tenant'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Tenant</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitEdit}>
            {renderTenantFormFields()}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Tenant'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Tenant</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{selectedTenant?.name}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsDeleteModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleConfirmDelete} disabled={submitting}>
              {submitting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </div>{/* end main list */}

      {/* ── Activity Panel ─────────────────────────────────────────── */}
      {activityTenant && (
        <div className="w-full lg:w-96 lg:flex-shrink-0 border-t pt-4 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-6">
          <div className="lg:sticky lg:top-4">
            <div className="flex items-center justify-between mb-4">
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Activity for</p>
                <p className="text-base font-semibold text-gray-900 truncate">{activityTenant.name}</p>
                <p className="text-xs text-muted-foreground">{activityTenant.property?.name} · Unit {activityTenant.unitNumber}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setActivityTenant(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <ActivityFeed
              tenantId={activityTenant._id}
              title=""
            />
          </div>
        </div>
      )}

      <LeaseSetupModal
        open={!!leaseTenant}
        onOpenChange={open => { if (!open) setLeaseTenant(null); }}
        tenantId={leaseTenant?._id}
        tenantName={leaseTenant?.name}
        propertyId={leaseTenant?.property?._id || leaseTenant?.property}
        propertyName={leaseTenant?.property?.name || leaseTenant?.property?.address}
        defaultUnitNumber={leaseTenant?.unitNumber || ''}
        onSaved={() => setLeaseTenant(null)}
      />
    </div>
  );
};

export default Tenants;
